import { useEffect, useState, useMemo, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { PlusIcon, PencilIcon, TrashIcon } from "@heroicons/react/24/outline";
import not_found from "../../../assets/images/sattva_misc/not_found.jpg"
import { motion } from "framer-motion";
import { Link } from "react-router-dom";

// Format Google Drive images
const formatDriveImage = (url) => {
  if (!url) return null;
  if (url.includes("drive.google.com")) {
    const match = url.match(/\/d\/([^/]+)/);
    if (match?.[1]) return `https://drive.google.com/thumbnail?id=${match[1]}&sz=w1200`;
  }
  return url;
};

const CaseStudiesSection = () => {
  const navigate = useNavigate();
  const [caseStudies, setCaseStudies] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const sliderRef = useRef(null);
  const [currentIndex, setCurrentIndex] = useState(0);
  const [modalOpen, setModalOpen] = useState(false);
  const [editingCS, setEditingCS] = useState(null);
  const [editorToken, setEditorToken] = useState(null);
  const isEditor = Boolean(editorToken);
  const CARD_GAP = 16; // gap-4
  const filteredCaseStudies = useMemo(() => {
    return caseStudies.filter(cs =>
      cs.title.toLowerCase().includes(search.toLowerCase()) ||
      cs.client?.toLowerCase().includes(search.toLowerCase())
    );
  }, [caseStudies, search]); // Only re-calculates if these change


  useEffect(() => {
    if (!filteredCaseStudies.length) return;

    const interval = setInterval(() => {
      setCurrentIndex((prev) => {
        const visibleCount = getVisibleCount();
        const maxIndex = Math.max(
          0,
          filteredCaseStudies.length - visibleCount
        );

        return prev >= maxIndex ? 0 : prev + 1;
      });
    }, 2000);

    return () => clearInterval(interval);
  }, [filteredCaseStudies]);

  const getCardWidth = () => {
    const container = sliderRef.current;
    if (!container) return 0;
    const card = container.querySelector("[data-card]");
    return card ? card.offsetWidth + CARD_GAP : 0;
  };

  useEffect(() => {
    const token = localStorage.getItem("Editor_token");
    setEditorToken(token);
  }, []);

  const getVisibleCount = () => {
    if (!sliderRef.current) return 1;

    const containerWidth = sliderRef.current.parentElement.offsetWidth;
    const cardWidth = getCardWidth();

    return Math.max(1, Math.floor(containerWidth / cardWidth));
  };


  const fetchCaseStudies = async () => {
    try {
      setLoading(true);
      const res = await fetch("https://sattva-backend.demetrix.in/case_study");
      const data = await res.json();
      if (data.status === "success") {
        setCaseStudies(
          data.data.filter(cs => cs.is_published && !cs.is_deleted && !cs.deleted_at)
        );
      } else {
        setCaseStudies([]);
        // alert(`Failed to fetch: ${data.message || "Unknown error"}`);
      }
    } catch (error) {
      console.error("Error fetching case studies:", error);
      // alert("Error fetching case studies: " + error.message);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchCaseStudies();
  }, []);

  // -----------------------------
  // Search filter
  // -----------------------------
  // Replace the old filteredCaseStudies line with this:

  // -----------------------------
  // Save Case Study (Add/Edit)
  // -----------------------------
  const handleFormSubmit = async (e) => {
    e.preventDefault();
    try {
      const isEdit = caseStudies.some(cs => cs.slug === editingCS.slug);
      const url = isEdit
        ? `https://sattva-backend.demetrix.in/case_study/${editingCS.slug}`
        : `https://sattva-backend.demetrix.in/case_study`;
      const method = isEdit ? "PUT" : "POST";

      const res = await fetch(url, {
        method,
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${editorToken}`,
        },
        body: JSON.stringify(editingCS),
      });

      const data = await res.json();

      if (data.status === "success") {
        alert(`Case Study ${isEdit ? "updated" : "added"} successfully!`);
        setModalOpen(false);
        setEditingCS(null);
        fetchCaseStudies();
      } else {
        alert(`Failed: ${data.message || "Unknown error"}`);
      }
    } catch (error) {
      console.error(error);
      alert("Error saving case study: " + error.message);
    }
  };

  // -----------------------------
  // Delete Case Study
  // -----------------------------
  const handleDelete = async (slug) => {
    if (!window.confirm("Are you sure to delete this case study?")) return;
    try {
      const res = await fetch(
        `https://sattva-backend.demetrix.in/case_study/${slug}`,
        {
          method: "DELETE",
          headers: {
            Authorization: `Bearer ${editorToken}`,
          },
        }
      );

      const data = await res.json();

      if (data.status === "success") {
        alert("Case Study deleted successfully!");
        fetchCaseStudies();
      } else {
        alert(`Failed to delete: ${data.message || "Unknown error"}`);
      }
    } catch (error) {
      console.error(error);
      alert("Error deleting case study: " + error.message);
    }
  };

  // -----------------------------
  // Media handlers
  // -----------------------------
  const addMedia = () => {
    setEditingCS(prev => ({
      ...prev,
      media: prev.media ? [...prev.media, { type: "image", url: "", caption: "" }] : [{ type: "image", url: "", caption: "" }]
    }));
  };

  const handleMediaChange = (index, field, value) => {
    const updatedMedia = [...editingCS.media];
    updatedMedia[index][field] = value;
    setEditingCS(prev => ({ ...prev, media: updatedMedia }));
  };

  const removeMedia = (index) => {
    const updatedMedia = editingCS.media.filter((_, i) => i !== index);
    setEditingCS(prev => ({ ...prev, media: updatedMedia }));
  };

  if (loading) {
    return (
      <div className="py-10 px-4 overflow-hidden">
        <div className="flex gap-4 animate-pulse">
          {[...Array(4)].map((_, idx) => (
            <div
              key={idx}
              className="flex-shrink-0 w-full sm:w-[calc(50%-0.75rem)] lg:w-[calc(33.333%-1rem)] aspect-[4/3] rounded-2xl bg-gray-200 dark:bg-gray-700 relative"
            >
              {/* Simulated image */}
              <div className="h-full w-full bg-gray-300 dark:bg-gray-600 rounded-2xl" />

              {/* Overlay simulating text */}
              <div className="absolute bottom-0 left-0 right-0 p-5 sm:p-6 flex flex-col gap-2">
                <div className="h-4 w-3/4 bg-gray-300 dark:bg-gray-600 rounded"></div>
                <div className="h-5 w-full bg-gray-300 dark:bg-gray-600 rounded"></div>
              </div>
            </div>
          ))}
        </div>
      </div>
    );
  }


  return (
    <section className="my-8 container" >
      {/* Header: Search + Add */}
      <div className="flex items-center gap-2 mb-6">
        {
          caseStudies?.length > 0 &&

          <input
            type="text"
            placeholder="Search case studies..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="flex-1 border border-gray-300 dark:border-gray-700 rounded-lg px-3 py-2 text-sm 
                     focus:outline-none focus:ring-2 focus:ring-green-500 
                     dark:bg-gray-900 dark:text-white"
          />
        }
        {isEditor && (
          <button
            onClick={() => {
              setEditingCS({
                slug: `new-cs-${Date.now()}`,
                title: "",
                client: "",
                summary: "",
                problem: "",
                solution: "",
                result: "",
                media: [],
              });
              setModalOpen(true);
            }}
            className="p-2 rounded-full bg-green-100 text-green-700 hover:bg-green-200 transition shrink-0"
            title="Add Case Study"
          >
            <PlusIcon className="w-5 h-5" />
          </button>
        )}

      </div>


      {/* Horizontal scroll container */}
      {
        caseStudies?.length > 0 ?

          <div className="overflow-hidden ">
            <div
              ref={sliderRef}
              className="flex gap-4 py-2 transition-transform duration-700 ease-in-out"
              style={{
                transform: `translateX(-${currentIndex * getCardWidth()}px)`,
              }}
            >

              {filteredCaseStudies?.map(cs => {
                const imageMedia = cs.media?.find(m => m.type === "image");
                const imageUrl = formatDriveImage(imageMedia?.url);

                return (
                  <div
                    data-card
                    key={cs.slug}
                    className="
    flex-shrink-0 
    w-full
    sm:w-[calc(50%-0.75rem)]
    lg:w-[calc(33.333%-1rem)]
    aspect-[4/3]
    rounded-2xl
    overflow-hidden
    relative
    group
  "
                  >

                    {/* Image / Placeholder */}
                    {imageUrl ? (
                      <img
                        src={imageUrl}
                        alt={cs.title}
                        className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-105"
                        loading="lazy"
                        referrerPolicy="no-referrer"
                        onError={(e) => {
                          e.currentTarget.src = "/placeholder.jpg"; // optional fallback
                        }}
                      />
                    ) : (
                      <div className="w-full h-full bg-gray-100 flex items-center justify-center text-gray-400 text-sm">
                        No image
                      </div>
                    )}

                    {/* Overlay */}
                    <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-black/30 to-transparent p-5 sm:p-6 flex flex-col justify-end">
                      {cs.client && (
                        <span className="text-xs sm:text-sm text-white/90 font-medium tracking-wide uppercase">
                          {cs.client}
                        </span>
                      )}
                      <h3 className="text-lg sm:text-xl md:text-2xl text-white font-semibold mt-1.5 line-clamp-2">
                        {cs.title}
                      </h3>
                    </div>

                    {/* Edit / Delete buttons – visible on hover for editors */}
                    {isEditor && (
                      <div className="absolute top-3 right-3 flex gap-2 opacity-0 group-hover:opacity-100 transition-opacity duration-300 z-20">
                        <button
                          onClick={(e) => {
                            e.stopPropagation();
                            setEditingCS(cs);
                            setModalOpen(true);
                          }}
                          className="p-2.5 rounded-full bg-white/90 text-blue-700 hover:bg-white hover:text-blue-800 shadow-md transition-colors"
                          title="Edit Case Study"
                        >
                          <PencilIcon className="w-5 h-5" />
                        </button>

                        <button
                          onClick={(e) => {
                            e.stopPropagation();
                            handleDelete(cs.slug);
                          }}
                          className="p-2.5 rounded-full bg-white/90 text-red-700 hover:bg-white hover:text-red-800 shadow-md transition-colors"
                          title="Delete Case Study"
                        >
                          <TrashIcon className="w-5 h-5" />
                        </button>
                      </div>
                    )}

                    {/* Clickable overlay to go to detail page */}
                    <div
                      onClick={() => navigate(`/case-study/${cs.slug}`)}
                      className="absolute inset-0 cursor-pointer z-10"
                    />
                  </div>
                );
              })}
            </div>
          </div>
          : <div className="w-full flex items-center justify-center px-4 ">
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5, ease: "easeOut" }}
              className="bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-700 rounded-2xl shadow-sm p-8 max-w-md w-full text-center"
            >
              {/* Illustration */}
              <motion.img
                src={not_found}
                alt="No case studies"
                className="mx-auto h-40 mb-6"
                animate={{ y: [0, -8, 0] }}
                transition={{ duration: 2, repeat: Infinity, ease: "easeInOut" }}
              />

              {/* Text */}
              <h3 className="text-xl font-semibold text-slate-800 dark:text-slate-100 mb-2">
                No case studies found
              </h3>

              <p className="text-slate-500 dark:text-slate-400 mb-6">
                We couldn’t find any case studies here. Once content is published, it’ll show up right away.
              </p>

              {/* CTA */}
              <div className="flex justify-center gap-3">
                <Link
                  to="/"
                  className="px-5 py-2.5 rounded-lg text-sm font-medium
              bg-slate-900 text-white dark:bg-white dark:text-slate-900
              hover:opacity-90 transition"
                >
                  Go Home
                </Link>
              </div>
            </motion.div>
          </div>
      }



      {/* Modal for Add/Edit */}
      {modalOpen && editingCS && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 overflow-auto p-4">
          <div className="bg-white dark:bg-gray-800 rounded-xl p-6 w-full max-w-2xl relative max-h-[90vh] overflow-y-auto mt-10">
            <h3 className="text-xl font-semibold mb-4">
              {caseStudies.some(cs => cs.slug === editingCS.slug) ? "Edit Case Study" : "Add Case Study"}
            </h3>

            <form onSubmit={handleFormSubmit} className="space-y-4 text-slate-600">
              <input
                type="text"
                name="title"
                placeholder="Title"
                value={editingCS.title}
                onChange={(e) => setEditingCS(prev => ({ ...prev, title: e.target.value }))}
                className="w-full border rounded-lg p-2"
                required
              />
              <input
                type="text"
                name="client"
                placeholder="Client"
                value={editingCS.client}
                onChange={(e) => setEditingCS(prev => ({ ...prev, client: e.target.value }))}
                className="w-full border rounded-lg p-2"
              />
              <textarea
                name="summary"
                placeholder="Summary"
                value={editingCS.summary}
                onChange={(e) => setEditingCS(prev => ({ ...prev, summary: e.target.value }))}
                className="w-full border rounded-lg p-2 whitespace-pre-line"
                rows={4}
              />
              <textarea
                name="problem"
                placeholder="Problem"
                value={editingCS.problem}
                onChange={(e) => setEditingCS(prev => ({ ...prev, problem: e.target.value }))}
                className="w-full border rounded-lg p-2 whitespace-pre-line"
                rows={3}
                required
              />

              <textarea
                name="solution"
                placeholder="Solution"
                value={editingCS.solution}
                onChange={(e) => setEditingCS(prev => ({ ...prev, solution: e.target.value }))}
                className="w-full border rounded-lg p-2 whitespace-pre-line"
                rows={3}
                required
              />

              <textarea
                name="result"
                placeholder="Result"
                value={editingCS.result}
                onChange={(e) => setEditingCS(prev => ({ ...prev, result: e.target.value }))}
                className="w-full border rounded-lg p-2 whitespace-pre-line"
                rows={3}
                required
              />


              {/* Media */}
              <div>
                <h4 className="font-semibold mb-2">Media (max 5)</h4>
                {editingCS.media?.map((m, idx) => (
                  <div key={idx} className="border rounded-lg p-2 mb-2 relative">
                    {/* Media Type */}
                    <select
                      value={m.type}
                      onChange={(e) => handleMediaChange(idx, "type", e.target.value)}
                      className="border rounded-lg p-1 w-full mb-1"
                    >
                      <option value="image">Image</option>
                      <option value="video">Video</option>
                    </select>

                    {/* URL input for both image and video */}
                    <input
                      type="text"
                      placeholder={m.type === "image" ? "Image URL" : "Video URL"}
                      value={m.url}
                      onChange={(e) => handleMediaChange(idx, "url", e.target.value)}
                      className="w-full border rounded-lg p-1 mb-1"
                      required
                    />

                    {/* Caption */}
                    <input
                      type="text"
                      placeholder="Caption"
                      value={m.caption}
                      onChange={(e) => handleMediaChange(idx, "caption", e.target.value)}
                      className="w-full border rounded-lg p-1"
                    />

                    {/* Remove media button */}
                    <button
                      type="button"
                      onClick={() => removeMedia(idx)}
                      className="absolute top-1 right-1 text-red-600 font-bold"
                    >
                      ✕
                    </button>
                  </div>
                ))}


                {editingCS.media?.length < 5 && (
                  <button
                    type="button"
                    onClick={addMedia}
                    className="bg-green-100 text-green-700 px-3 py-1 rounded-lg hover:bg-green-200 transition"
                  >
                    + Add Media
                  </button>
                )}
              </div>

              <div className="flex gap-2 justify-end">
                <button type="submit" className="bg-blue-600 text-white px-4 py-2 rounded-lg hover:bg-blue-700 transition">
                  Save
                </button>
                <button
                  type="button"
                  onClick={() => { setModalOpen(false); setEditingCS(null); }}
                  className="bg-gray-300 text-gray-800 px-4 py-2 rounded-lg hover:bg-gray-400 transition"
                >
                  Cancel
                </button>
              </div>
            </form>

            <button
              onClick={() => { setModalOpen(false); setEditingCS(null); }}
              className="absolute top-2 right-2 text-gray-500 hover:text-gray-800 font-bold text-xl"
            >
              ✕
            </button>
          </div>
        </div>
      )}
    </section>
  );
};

export default CaseStudiesSection;
